import React from "react"
import { Link } from "gatsby"
import { get } from "lodash"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import {
  downloadContainer,
  inlineDownloadTitle,
  inlineImage,
  formOuterWrapper,
  formSubmit,
  Ribbon,
  RibbonTopRight,
  listItem,
  formWrapper,
  RichTextDefault,
} from "./rich-text-content.module.css"
import { GatsbyImage } from "gatsby-plugin-image"
import Table from "./table/table"
import Video from "./video/video"
import linkBuilder from "../../modules/linkBuilder"
import { FaCheck } from "react-icons/fa"
import DisplayTracking from "../tracking/display-tracking"
import { eventTracking, trackExternalLink } from "../../services/tracking"
import { getUid } from "../../services/auth"
import Outgrow from "../../components/outgrow/outgrow"
import SpotifyEmbed from "../../components/spotify-embed/spotify-embed"
import Visme from "../visme/visme"
import ExplanationEmbed from "./explanation/explanation"
import QuoteEmbed from "./quote/quote"
import {
  dataLayer,
  trackEvent as analyticsTrackEvent,
} from "../../modules/analytics"
import InlineImage from "../inline-image/inline-image"
import CustomForm from "../custom-form/custom-form"
import HubSpotForm from "../hubspot-form/hubspot-form"

const RichTextContent = ({
  content,
  pageTitle,
  pageContentfulId,
  addListMarker,
  displayPagePath,
  injectAfterH2, // NEW PROP
  defaultRichText = false,
}) => {
  let h2Count = 0
  const shouldInject = typeof injectAfterH2 !== "undefined"

  // Copy the original renderNode, but override HEADING_2
  const baseRenderNode = {
    [INLINES.HYPERLINK]: (node) => {
      const link = get(node, "data.uri")
      const linkText = get(node, "content[0].value")
      const trackEvent = () => {
        analyticsTrackEvent("richtext_external_link", { url: link })
        dataLayer({
          event: "website-interaction",
          category: "rich-text-content",
          action: `external-link:${link}`,
        })
      }

      /* 
        We can't use <Link to> because we don't know if this link exist
        and with <Link> URL won't get redirected by redirection.io. 
      */
      if (
        /^http(s|):\/\/(www\.|)mkbservicedesk.nl/.test(link) ||
        link.startsWith("/")
      )
        return <a href={link}>{linkText}</a>
      else
        return (
          <a
            className="external-link"
            href={link}
            rel="noopener noreferrer"
            onClick={trackEvent}
            target="_blank"
          >
            {linkText}
          </a>
        )
    },
    [INLINES.ENTRY_HYPERLINK]: (node) => {
      const contentType = get(node, "data.target.__typename")
      const linkText = get(node, "content[0].value")
      let internalLink, externalLink, linkTitle

      switch (contentType) {
        case "ContentfulArticle":
          internalLink = linkBuilder.article(node.data.target)
          linkTitle = get(node, "data.target.title")
          break
        case "ContentfulDesk":
          internalLink = linkBuilder.desk(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulDeskCategory":
          internalLink = linkBuilder.deskCategory(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulDownload":
          internalLink = linkBuilder.download(node.data.target)
          linkTitle = get(node, "data.target.title")
          break
        case "ContentfulCampaign":
          internalLink = linkBuilder.campaign(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulExternalLink":
          externalLink = node.data?.target?.url
          linkTitle = get(node, "data.target.title")
          break
        case "ContentfulProductBeta":
          internalLink = linkBuilder.product(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulPage":
          internalLink = linkBuilder.page(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulSponsor":
          externalLink = node.data?.target?.url
          linkTitle = get(node, "data.target.name")
          break
        case "ContentfulOutgrow":
          internalLink = linkBuilder.tool(node.data.target)
          linkTitle = get(node, "data.target.name")
          break
        default:
          if (!contentType) {
            return <>{linkText}</>
          }
          break
      }

      if (internalLink)
        return (
          <Link to={internalLink} title={linkTitle}>
            {linkText}
          </Link>
        )
      else if (externalLink) {
        const contentTypeToContentfulType = (input) => {
          // Remove "Contentful" from the string
          let result = input.replace("Contentful", "")

          // Change the first character of the resulting string to lowercase
          if (result.length > 0) {
            result = result.charAt(0).toLowerCase() + result.slice(1)
          }
          return result
        }
        const trackEvent = async () => {
          analyticsTrackEvent("richtext_external_link", { url: externalLink })
          eventTracking("click", {
            contentfulId: get(node, "data.target.contentful_id"),
            contentfulType: contentTypeToContentfulType(contentType),
            contentfulTitle: get(node, "data.target.title"),
            displayPageContentfulId: pageContentfulId,
            displayPosition: "richTextContent",
          })
          dataLayer({
            event: "website-interaction",
            category: "rich-text-content",
            action: `external-link:${externalLink}`,
          })
          trackExternalLink(
            await getUid(),
            get(node, "data.target.contentful_id"),
            externalLink
          )
        }

        const relValue =
          node.data?.target?.sponsored || contentType
            ? "sponsored noreferrer noopener"
            : "noreferrer noopener"

        return (
          <a
            href={externalLink}
            title={linkTitle}
            rel={relValue}
            target="_blank"
            onClick={trackEvent}
          >
            {linkText}
          </a>
        )
      }
    },
    [INLINES.ASSET_HYPERLINK]: (node) => {
      const fileUrl = get(node, "data.target.file.url")
      const linkText = get(node, "content[0].value")

      if (fileUrl)
        return (
          <Link to={fileUrl} title={linkText}>
            {linkText}
          </Link>
        )
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      /* Legacy support for incorrectly embedded video entries */
      return <Video data={get(node, "data.target")}></Video>
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node, data) => {
      const target = get(node, "data.target")
      if (!target) return <></>

      if (target.__typename === "ContentfulOutgrow") {
        return <Outgrow id={node.data.target.outgrowId} />
      } else if (target.__typename === "ContentfulSpotify") {
        return (
          <SpotifyEmbed
            title={node.data.target.title}
            link={node.data.target.link}
          />
        )
      } else if (target.__typename === "ContentfulVisme") {
        return (
          <Visme id={node.data.target.vismeId} title={node.data.target.title} />
        )
      } else if (target.__typename === "ContentfulTable") {
        return <Table data={node.data.target.tableData} />
        /* Video embed */
      } else if (target.__typename === "ContentfulVideo") {
        return <Video data={get(node.data, "target")}></Video>
        /* Explanation embed */
      } else if (target.__typename === "ContentfulExplanation") {
        return <ExplanationEmbed data={get(node.data, "target")} />
        /* Quote embed */
      } else if (target.__typename === "ContentfulQuote") {
        return <QuoteEmbed data={get(node.data, "target")} />
        /* Inline image embed */
      } else if (target.__typename === "ContentfulInlineImage") {
        return (
          <DisplayTracking
            contentfulId={target.contentful_id}
            contentTitle={target.title}
            contentType="inlineImage"
            displayPosition="richTextContent"
            displayPageContentfulId={pageContentfulId}
            displayPageTitle={pageTitle}
          >
            <InlineImage
              data={get(node.data, "target")}
              parentData={{ pageContentfulId, pageTitle }}
            />
          </DisplayTracking>
        )
        /* Download embed */
      } else if (target.__typename === "ContentfulDownload") {
        let contentfulId = target.contentful_id
        let downloadTitle = target.title
        let formFields = target.formFields
        let width

        return (
          <DisplayTracking
            contentfulId={contentfulId}
            contentTitle={downloadTitle}
            contentType="download"
            displayPosition="richTextContent"
            displayPageContentfulId={pageContentfulId}
            displayPageTitle={pageTitle}
            formFields={formFields}
          >
            <div
              className={`${downloadContainer} rich-text-download-container clearfix`}
            >
              <h3 className={inlineDownloadTitle}>
                Whitepaper: <span>{downloadTitle}</span>
              </h3>
              {target.image && (
                <GatsbyImage
                  Tag={"section"}
                  alt={target.image.title ? target.image.title : ""}
                  className={inlineImage}
                  width={width}
                  style={{ maxWidth: width, margin: `0 auto` }}
                  image={target.image.gatsbyImageData}
                  // fluid={{
                  //   alt: title,
                  //   aspectRatio: width / height,
                  //   src: sources[1],
                  //   srcSet: sources.toString(),
                  //   sizes: width.toString(),
                  // }}
                />
              )}

              <div className={formOuterWrapper}>
                {/* <DownloadForm
                contentfulId={contentfulId}
                title={downloadTitle}
                darkBackground={false}
                fields={formFields}
                asset={asset}
                sponsor={sponsor}
                displayPosition="richTextContent"
                displayPageContentfulId={pageContentfulId}
                displayPageTitle={pageTitle}
                displayPagePath={displayPagePath}
                trackEvent={{
                  'event': 'website-interaction',
                  'category': 'call-to-action',
                  'action': `download`,
                  'label': `position:rich-text-content`
                }} /> */}
                <a
                  href={`${linkBuilder.absolute(
                    linkBuilder.download(target)
                  )}?utm_source=mkbsd&utm_medium=website&utm_campaign=inline`}
                  className={`${formSubmit} download-form-submit`}
                >
                  {target.downloadType === "Whitepaper"
                    ? "Download"
                    : "Contactformulier"}
                </a>
              </div>

              <div className={`${Ribbon} ${RibbonTopRight}`}>
                <span>GRATIS</span>
              </div>
            </div>
          </DisplayTracking>
        )
      } else if (target.__typename === "ContentfulForm") {
        if (target.hubSpotFormId) {
          return (
            <div className={formWrapper}>
              <HubSpotForm formId={target.hubSpotFormId} />
            </div>
          )
        } else if (target.form) {
          return (
            <div className={formWrapper}>
              <CustomForm
                formData={target.form}
                formPosition="article_page_richtext"
              />
            </div>
          )
        }
        return <></>
      } else {
        console.error(
          "There was an error displaying an unsported embedded entry"
        )
        return <></>
      }
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const title = get(node.data, "target.title")
      const gatsbyImageData = get(node.data, "target.gatsbyImageData")
      const imageFile = get(node.data, "target.file")
      if (gatsbyImageData)
        return (
          <GatsbyImage
            Tag={"section"}
            className={inlineImage}
            image={gatsbyImageData}
            alt={title}
          />
        )

      if (imageFile) return <img src={imageFile.url} alt={title} />

      return <></>
    },
  }

  const renderOptions = {
    renderNode: {
      ...baseRenderNode,
      [BLOCKS.HEADING_2]: (node, children) => {
        h2Count++
        const h2Element =
          node.nodeType === "heading-2" &&
          node.content.length === 1 &&
          node.content[0].nodeType === "text" &&
          node.content[0].value !== "" ? (
            <h2
              id={`toc-${node.content[0].value
                .replace(/[^a-z0-9A-Z]/g, "")
                .toLowerCase()}`}
            >
              {textOnly(children)}
            </h2>
          ) : (
            <h2>{textOnly(children)}</h2>
          )
        if (shouldInject && h2Count === 3) {
          return [injectAfterH2, h2Element]
        }
        return h2Element
      },
    },
  }

  if (addListMarker) {
    renderOptions.renderNode[BLOCKS.LIST_ITEM] = (node, children) => {
      return (
        <li className={listItem}>
          <span className={"fa-wrapper"}>
            <FaCheck />
          </span>{" "}
          {node.content[0].content[0].value}
        </li>
      )
    }
  }

  if (defaultRichText) {
    return (
      <div className={RichTextDefault}>
        {renderRichText(content, renderOptions)}
      </div>
    )
  }
  return renderRichText(content, renderOptions)
}

export default RichTextContent

function textOnly(children) {
  try {
    return children
      .map((child) => {
        if (!child || !child.props) return child
        if (typeof child.props.children === "string")
          return child.props.children
        else return child.props.children.props.children
      })
      .join("")
  } catch (e) {
    return children
  }
}
